/**
 * Which channel calls back.
 *
 * This page used to open with a numbered list of rules under the sentence "the first
 * enabled rule that matches wins", and Arun's reaction was the correct one: "the
 * routing tab was hard and made me anxious to knpw whats going on. amde me feels
 * tupid not ebing abel to udnetsand that."
 *
 * The reason it was unreadable was not layout. The rules matched on (agent, channel,
 * outcome) with blanks as wildcards, and the resolver took the MOST SPECIFIC match,
 * using priority only to break ties — so rule 3 could beat rule 1 while sitting below
 * it on screen. A page whose ordering does not predict its own behaviour cannot be
 * understood, only memorised.
 *
 * So the rules are gone. Arun: "For every agent channel -> map that same channel for
 * call back. User can override it if they need. this way you wont need rules." A
 * callback dials back on the agent channel the conversation came from. That needs no
 * configuration and no row, which means this screen has one job: list the channels and
 * say, per channel, whether anything unusual is happening to it.
 *
 * Two facts still have to be visible, because both silently stop a callback:
 *
 *  - The channel that took the original call often *cannot* place the callback. A
 *    campaign dialler runs on a channel whose `call_initiator` is EXTERNAL and 8loop
 *    refuses click-to-call on those. That is exactly what an override is for, and the
 *    row says so rather than waiting for the dial to fail.
 *  - This list is a local cache, not a live read. A channel created in 8loop five
 *    minutes ago is not here yet, so its age is printed and refreshing it is a button.
 */

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronRight,
  CornerUpRight,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { SettingsSection } from "@/components/ui/settings-section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  errorMessage,
  type AccountConfig,
  type ChannelDial,
  type EightloopChannel,
  type RoutedChannel,
} from "@/lib/api";
import { relativeTime } from "@/lib/labels";
import {
  useDeleteOverride,
  useEightloopAgents,
  useRefreshChannels,
  useRouteResolution,
  useSaveOverride,
  type OverrideInput,
} from "@/lib/queries";

export function RoutesSection({
  accountId,
  config,
}: {
  accountId: string;
  config: AccountConfig;
}) {
  const resolution = useRouteResolution(accountId);
  const refresh = useRefreshChannels(accountId);
  const del = useDeleteOverride(accountId);
  const [editing, setEditing] = useState<RoutedChannel | null>(null);
  const [showQuiet, setShowQuiet] = useState(false);

  /*
    One handler for both lists.

    The folded "can't send a callback" list used to call `del.mutate(c.channel_id)` bare —
    no onSuccess, no onError — so clearing an override there did its work in total
    silence, and a failure did too. Two call sites of the same action with different
    feedback is how one of them ends up being the broken one.
  */
  const clearOverride = (channelId: string) =>
    del.mutate(channelId, {
      onSuccess: () =>
        toast.success(`Override removed — channel ${channelId} dials back on itself again.`),
      onError: (e) => toast.error(errorMessage(e)),
    });

  const channels = resolution.data?.channels ?? [];

  // Split rather than filter. A channel that has never sent a callback and cannot send
  // one — inbound, archived, or deleted in 8loop since — is noise in the common case
  // and essential in the "why isn't this one here?" case, so it is folded, counted and
  // one click away. Never silently dropped.
  const { active, quiet } = useMemo(() => {
    const active: RoutedChannel[] = [];
    const quiet: RoutedChannel[] = [];
    for (const c of channels) {
      const couldSource = c.live && !c.inbound_only && !c.agent_archived;
      if (c.callbacks_seen > 0 || couldSource) active.push(c);
      else quiet.push(c);
    }
    return { active, quiet };
  }, [channels]);

  const groups = useMemo(() => groupByAgentName(active), [active]);
  const broken = active.filter((c) => isBroken(c.dial));
  const overrides = channels.filter((c) => c.dial.how === "override");
  const redundant = channels.filter((c) => c.dial.redundant);

  return (
    <>
      <SettingsSection
        title="Where callbacks go"
        description="A callback dials back on the agent channel the conversation came in on. That is the default for every channel and needs no setup — this list exists so you can see it is true, and override the ones where it isn't right."
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {resolution.data?.refreshed_at
                ? `Channel list read from 8loop ${relativeTime(resolution.data.refreshed_at)}`
                : "Channel list has never been read from 8loop"}
              {resolution.data && !resolution.data.checked_phone_numbers && (
                <> — this API key cannot read phone numbers, so caller IDs are unknown</>
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={refresh.isPending}
              onClick={() =>
                refresh.mutate(undefined, {
                  onSuccess: (r) => toast.success(r.message),
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              <RefreshCw className={refresh.isPending ? "size-3.5 animate-spin" : "size-3.5"} />
              {refresh.isPending ? "Refreshing…" : "Refresh channels"}
            </Button>
          </div>
        }
      >
        {resolution.isPending && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {resolution.isError && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Couldn't work out where callbacks would go: {errorMessage(resolution.error)}
            </AlertDescription>
          </Alert>
        )}

        {/*
          A failed refresh does not empty the list — yesterday's channels plus "we tried
          and 8loop said no" is strictly more useful than a blank page. Which makes
          saying the refresh failed mandatory, or the stale list reads as current.
        */}
        {resolution.data?.refresh_error && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              The last attempt to read this tenant's channels failed, so the list below is
              whatever we had before that
              {resolution.data.refreshed_at ? ` (${relativeTime(resolution.data.refreshed_at)})` : ""}.
              A channel created in 8loop since then is missing. 8loop said:{" "}
              {resolution.data.refresh_error}
            </AlertDescription>
          </Alert>
        )}

        {broken.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              {broken.length === 1
                ? "1 channel below cannot place a callback"
                : `${broken.length} channels below cannot place a callback`}
              , so callbacks from{" "}
              {broken.length === 1 ? "it" : "them"} will be recorded and never dialled. Send{" "}
              {broken.length === 1 ? "it" : "each"} somewhere else with an override.
            </AlertDescription>
          </Alert>
        )}

        {redundant.length > 0 && (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription>
              {redundant.length === 1 ? "1 override points" : `${redundant.length} overrides point`}{" "}
              back at the same channel, which is what would happen anyway. Removing{" "}
              {redundant.length === 1 ? "it" : "them"} changes nothing except how much there is to
              read here.
            </AlertDescription>
          </Alert>
        )}

        {resolution.data && channels.length === 0 && (
          <EmptyState
            size="compact"
            title="No channels to show"
            description="Nothing on this tenant has sent a callback, and no channels have been read from 8loop. Press Refresh channels — if that fails, the API key is the thing to fix first."
          />
        )}

        {groups.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {groups.map((g) => (
              <li key={g.key} className="px-4 py-3">
                <p className="text-sm font-medium">
                  {g.agent_name ?? (g.agent_id ? `Agent ${g.agent_id}` : "Unknown agent")}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {g.channels.length} channel{g.channels.length === 1 ? "" : "s"}
                    {g.callbacks_seen > 0 &&
                      ` · ${g.callbacks_seen} callback${g.callbacks_seen === 1 ? "" : "s"} so far`}
                  </span>
                </p>
                <ul className="mt-2 space-y-3">
                  {g.channels.map((c) => (
                    <ChannelRow
                      key={c.channel_id}
                      channel={c}
                      onOverride={() => setEditing(c)}
                      onClear={() => clearOverride(c.channel_id)}
                      // Per row, not per page: a shared `isPending` disables the clear
                      // button on every channel while one of them is working.
                      clearing={del.isPending && del.variables === c.channel_id}
                    />
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}

        {quiet.length > 0 && (
          <div>
            <Button variant="ghost" size="sm" onClick={() => setShowQuiet((v) => !v)}>
              {showQuiet ? "Hide" : "Show"} {quiet.length} channel
              {quiet.length === 1 ? "" : "s"} that can't send a callback
            </Button>
            {showQuiet && (
              <ul className="mt-2 space-y-3 rounded-lg border border-border px-4 py-3">
                {quiet.map((c) => (
                  <ChannelRow
                    key={c.channel_id}
                    channel={c}
                    onOverride={() => setEditing(c)}
                    onClear={() => clearOverride(c.channel_id)}
                    clearing={del.isPending && del.variables === c.channel_id}
                  />
                ))}
              </ul>
            )}
          </div>
        )}

        {/*
          Counted, because "no overrides" is the healthy state and an empty list is
          otherwise indistinguishable from a list that failed to load.
        */}
        {resolution.data && (
          <p className="text-xs text-muted-foreground">
            {overrides.length === 0
              ? "No overrides. Every channel calls back on itself."
              : `${overrides.length} channel${overrides.length === 1 ? "" : "s"} ${
                  overrides.length === 1 ? "has" : "have"
                } an override; the rest call back on themselves.`}
          </p>
        )}
      </SettingsSection>

      {editing && (
        <OverrideDialog
          accountId={accountId}
          config={config}
          channel={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

// ------------------------------------------------- one agent's channels ----

/**
 * The same list, narrowed to one agent, for that agent's own page.
 *
 * Arun: *"think what is account level and waht is agent level and move agent level things to
 * under each agent so its easy to manage."* A channel belongs to exactly one agent, so which
 * channel calls back is agent-level; reading the tenant's channel list is account-level and
 * stays on the Agents tab.
 *
 * This is a narrowing rather than a second implementation on purpose: it reuses `ChannelRow`,
 * `Destination` and `OverrideDialog`, so an override saved here and one saved on the account-wide
 * list are the same write with the same wording. Two copies of an 800-line screen is how one of
 * them silently stops matching the other.
 */
export function AgentChannelsSection({
  accountId,
  config,
  agentId,
}: {
  accountId: string;
  config: AccountConfig;
  agentId: string;
}) {
  const resolution = useRouteResolution(accountId);
  const refresh = useRefreshChannels(accountId);
  const del = useDeleteOverride(accountId);
  const [editing, setEditing] = useState<RoutedChannel | null>(null);

  const mine = useMemo(
    () => (resolution.data?.channels ?? []).filter((c) => c.agent_id === agentId),
    [resolution.data, agentId],
  );
  const broken = mine.filter((c) => isBroken(c.dial));
  const usable = mine.filter((c) => !isBroken(c.dial));
  const overridden = usable.filter((c) => c.dial.how === "override").length;

  const clearOne = (channelId: string) =>
    del.mutate(channelId, {
      onSuccess: () =>
        toast.success(`Override removed — channel ${channelId} dials back on itself again.`),
      onError: (e) => toast.error(errorMessage(e)),
    });

  return (
    <>
      <SettingsSection
        title="Can this agent place the callback?"
        description="A callback goes back out on whichever channel its call came in on, which needs no setup. The only thing to do here is fix a channel 8loop will refuse — a campaign dialler, usually — by sending its callbacks out on one that works."
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {resolution.data?.refreshed_at
                ? `Channel list read from 8loop ${relativeTime(resolution.data.refreshed_at)}`
                : "Channel list has never been read from 8loop"}
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={refresh.isPending}
              onClick={() =>
                refresh.mutate(undefined, {
                  onSuccess: (r) => toast.success(r.message),
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              <RefreshCw className={refresh.isPending ? "size-3.5 animate-spin" : "size-3.5"} />
              {refresh.isPending ? "Refreshing…" : "Refresh channels"}
            </Button>
          </div>
        }
      >
        {resolution.isPending && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {resolution.isError && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Couldn't work out where this agent's callbacks would go: {errorMessage(resolution.error)}
            </AlertDescription>
          </Alert>
        )}

        {resolution.data && mine.length === 0 && (
          <EmptyState
            size="compact"
            title="No channels on this agent"
            description={
              resolution.data.refreshed_at
                ? "8loop's channel list has no channel belonging to this agent, so there is nothing for a callback to dial out on and nothing that could have sent one in. If you have just created the channel in 8loop, press Refresh channels."
                : "This tenant's channels have never been read. Press Refresh channels — if that fails, the API key is the thing to fix first."
            }
          />
        )}

        {/* The whole reason this section exists on an agent's page: an agent whose only
            channel is a campaign dialler looks perfectly set up until the first callback
            silently lands in Needs attention. */}
        {mine.length > 0 && usable.length === 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              None of this agent's channels can place a call, so every callback it sends will be
              recorded and never dialled. Send{" "}
              {mine.length === 1 ? "it" : "each of them"} to a channel that can dial, with an
              override.
            </AlertDescription>
          </Alert>
        )}

        {mine.length > 0 && usable.length > 0 && broken.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              {broken.length === 1
                ? "1 of this agent's channels cannot place a callback"
                : `${broken.length} of this agent's channels cannot place a callback`}
              , so callbacks arriving on {broken.length === 1 ? "it" : "them"} are recorded and
              never dialled.
            </AlertDescription>
          </Alert>
        )}

        {/*
          Broken first, working folded. Arun, looking at seven rows on one agent — six of them
          saying "8loop accepts this" and one saying it will refuse — *"all of these cahnnels
          beling to same agent … ?"* The list was six lines of good news wrapped around the one
          line that needed him. A callback dials back on whichever channel its call came in on,
          so every channel does matter; what does not matter is reading six identical
          confirmations to find the exception.
        */}
        {broken.length > 0 && (
          <ul className="space-y-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3">
            {broken.map((c) => (
              <ChannelRow
                key={c.channel_id}
                channel={c}
                onOverride={() => setEditing(c)}
                onClear={() => clearOne(c.channel_id)}
                clearing={del.isPending && del.variables === c.channel_id}
              />
            ))}
          </ul>
        )}

        {usable.length > 0 && (
          <details className="group rounded-lg border border-border px-4 py-3">
            <summary className="hover:text-foreground marker:content-[''] flex cursor-pointer list-none items-center gap-1.5 text-sm select-none">
              <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
              <span>
                {usable.length === 1
                  ? "1 channel on this agent can place a callback"
                  : `${usable.length} channels on this agent can place a callback`}
                {overridden > 0
                  ? ` · ${overridden} sent elsewhere`
                  : " · each dials back on itself, which needs no setup"}
              </span>
            </summary>
            <ul className="mt-3 space-y-3">
              {usable.map((c) => (
                <ChannelRow
                  key={c.channel_id}
                  channel={c}
                  onOverride={() => setEditing(c)}
                  onClear={() => clearOne(c.channel_id)}
                  clearing={del.isPending && del.variables === c.channel_id}
                />
              ))}
            </ul>
          </details>
        )}
      </SettingsSection>

      {editing && (
        <OverrideDialog
          accountId={accountId}
          config={config}
          channel={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------------- rows ----

function ChannelRow({
  channel,
  onOverride,
  onClear,
  clearing,
}: {
  channel: RoutedChannel;
  onOverride: () => void;
  onClear: () => void;
  clearing: boolean;
}) {
  const c = channel;
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-2">
      <div className="min-w-[220px] flex-1">
        <p className="text-sm">
          {c.channel_label ?? (c.live ? "unnamed channel" : "channel")}
          <span className="font-mono text-xs text-muted-foreground"> · {c.channel_id}</span>
        </p>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {c.callbacks_seen > 0 && (
            <span>
              {c.callbacks_seen} callback{c.callbacks_seen === 1 ? "" : "s"}
            </span>
          )}
          {c.inbound_only && (
            <Badge variant="outline" className="text-[10px]">
              inbound — answers calls, never starts one
            </Badge>
          )}
          {c.agent_archived && (
            <Badge variant="outline" className="text-[10px]">
              agent archived — will not send another callback
            </Badge>
          )}
          {/* Only claimable from a list we actually got. Absent from a failed read is
              not evidence of deletion. */}
          {!c.live && (
            <Badge variant="outline" className="text-[10px]">
              not in the last channel list
            </Badge>
          )}
          {c.override_disabled && (
            <Badge variant="outline" className="text-[10px] text-warning">
              override switched off — dialling back on itself
            </Badge>
          )}
        </p>
      </div>

      <ArrowRight className="mt-1 size-3.5 shrink-0 text-muted-foreground" />

      <div className="min-w-[260px] flex-1">
        <Destination dial={c.dial} />
      </div>

      <div className="flex shrink-0 gap-1">
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onOverride}>
          <CornerUpRight className="size-3.5" />
          {c.dial.how === "override" ? "Change" : "Send elsewhere"}
        </Button>
        {c.dial.route_id && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={clearing}
            onClick={onClear}
          >
            <RotateCcw className="size-3.5" />
            Back to itself
          </Button>
        )}
      </div>
    </li>
  );
}

/** The dial side of one channel: what rings, from what number, and whether it works. */
function Destination({ dial }: { dial: ChannelDial }) {
  if (dial.how === "unroutable") {
    return (
      <>
        <p className="text-sm font-medium text-destructive">
          nothing — callbacks from here land in Needs attention
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{dial.unroutable_reason}</p>
      </>
    );
  }

  return (
    <>
      <p className="text-sm">
        {dial.how === "same_channel" ? (
          <span className="text-muted-foreground">dials back on this same channel</span>
        ) : (
          <>
            <span className="font-medium">
              {dial.channel_label ?? dial.routing_identifier}
            </span>
            {/* Only when it adds something. 8loop channels are often named after their
                agent, and "MultiplyRR — Feedback on MultiplyRR — Feedback" reads like a
                rendering bug. */}
            {dial.agent_name && dial.agent_name !== dial.channel_label && (
              <span className="text-muted-foreground"> on {dial.agent_name}</span>
            )}
          </>
        )}
      </p>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        {dial.caller_id ? (
          <span>
            rings from <span className="font-mono">{dial.caller_id}</span>
          </span>
        ) : (
          <span>caller ID unknown</span>
        )}
        {dial.provider_type && <span>via {dial.provider_type}</span>}
        {dial.dialable === true && (
          <span className="inline-flex items-center gap-1 text-success">
            <Check className="size-3" />
            8loop accepts this
          </span>
        )}
      </p>

      {dial.redundant && (
        <p className="mt-1 text-xs text-warning">
          This override points at the channel it came from, so it does nothing.
        </p>
      )}
      {dial.missing_from_tenant && (
        <p className="mt-1 text-xs text-destructive">
          8loop no longer lists this channel, so the dial will fail. The override still points at{" "}
          <span className="font-mono">{dial.routing_identifier}</span>.
        </p>
      )}
      {dial.dialable === false && (
        <p className="mt-1 text-xs text-destructive">
          8loop will refuse this dial. {dial.blocked_reason}
        </p>
      )}
      {dial.warnings.map((w) => (
        <p key={w} className="mt-1 text-xs text-warning">
          {w}
        </p>
      ))}
    </>
  );
}

/** A destination that will not actually place a call. */
function isBroken(dial: ChannelDial): boolean {
  return dial.how === "unroutable" || dial.dialable === false || dial.missing_from_tenant;
}

type AgentGroup = {
  key: string;
  agent_id: string | null;
  agent_name: string | null;
  channels: RoutedChannel[];
  callbacks_seen: number;
};

/**
 * Grouped by agent, because the agent is the unit an FDE thinks in and equentis has
 * fifteen channels across seven agents. Flat, the list was fifteen rows of which
 * thirteen said the same thing.
 */
function groupByAgentName(channels: RoutedChannel[]): AgentGroup[] {
  const byAgent = new Map<string, RoutedChannel[]>();
  for (const c of channels) {
    const k = c.agent_id ?? c.agent_name ?? "__none__";
    const list = byAgent.get(k);
    if (list) list.push(c);
    else byAgent.set(k, [c]);
  }
  return [...byAgent.entries()]
    .map(([key, list]) => ({
      key,
      agent_id: list[0]!.agent_id,
      agent_name: list[0]!.agent_name,
      // Broken first inside a group: the row that needs a decision should not be
      // below three rows that are fine.
      channels: [...list].sort(
        (a, b) =>
          Number(isBroken(b.dial)) - Number(isBroken(a.dial)) ||
          b.callbacks_seen - a.callbacks_seen ||
          a.channel_id.localeCompare(b.channel_id),
      ),
      callbacks_seen: list.reduce((n, c) => n + c.callbacks_seen, 0),
    }))
    .sort(
      (a, b) =>
        Number(b.channels.some((c) => isBroken(c.dial))) -
          Number(a.channels.some((c) => isBroken(c.dial))) ||
        b.callbacks_seen - a.callbacks_seen ||
        (a.agent_name ?? "").localeCompare(b.agent_name ?? ""),
    );
}

// ---------------------------------------------------------------- editor ----

/**
 * One override, for one channel.
 *
 * The source is not editable here — it is the row you clicked. That removes the whole
 * class of mistake the old dialog invited, where an agent and a channel could be picked
 * that did not belong together and the rule silently matched nothing.
 */
/** The picker's value for "leave it alone", which is what happens with no override at all. */
const SAME_CHANNEL = "__same_channel__";

/**
 * Where one channel's callbacks should dial out.
 *
 * ## The default is the same channel, and the picker now says so (2026-09-03)
 *
 * Arun: *"the agent page channel list should show only that agents channels, defaulting to the
 * same channel with the option to pick another agent/channel."* The list above already shows
 * one agent's channels; this dialog was the half that did not follow. It opened with an empty
 * "Choose a channel" against a flat, alphabetical list of every channel on the tenant — 129
 * agents' worth — with no indication that dialling back on itself was both the default and
 * almost always right, and no way to say "actually, leave it alone" without hunting for the
 * row's own entry among hundreds.
 *
 * So: the first option IS the default, selected when there is no override, and choosing it on
 * a channel that has one removes it. This agent's own channels come first, under a heading
 * that says whose they are. Every other agent follows, because sending a callback to a
 * different agent is a real thing to want — it is exactly what a campaign-dialler channel
 * needs — just not the common case.
 */
function OverrideDialog({
  accountId,
  config,
  channel,
  onClose,
}: {
  accountId: string;
  config: AccountConfig;
  channel: RoutedChannel;
  onClose: () => void;
}) {
  const save = useSaveOverride(accountId);
  const del = useDeleteOverride(accountId);
  const agents = useEightloopAgents(accountId);
  const existing = config.routes.find((r) => r.source_channel_id === channel.channel_id) ?? null;

  const [form, setForm] = useState<OverrideInput>(() => ({
    source_channel_id: channel.channel_id,
    source_agent_name: channel.agent_name,
    source_channel_label: channel.channel_label,
    dial_routing_identifier: existing?.dial_routing_identifier ?? "",
    dial_agent_id: existing?.dial_agent_id ?? null,
    dial_agent_name: existing?.dial_agent_name ?? null,
    dial_channel_label: existing?.dial_channel_label ?? null,
    enabled: existing?.enabled ?? true,
  }));

  /**
   * What the picker is showing, which is not quite the form: `SAME_CHANNEL` is a real choice
   * with no routing identifier behind it. Held separately so "the default" can be selected
   * and pressed rather than being the absence of a selection — an empty select cannot be the
   * default, because nobody can tell an untouched control from a deliberate one.
   */
  const [picked, setPicked] = useState<string>(
    existing?.dial_routing_identifier ?? SAME_CHANNEL,
  );

  const set = <K extends keyof OverrideInput>(key: K, value: OverrideInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));


  // `channels` is a flat top-level array keyed by `agent_id`. It is NOT nested under
  // each agent — believing otherwise white-screened this dialog for every account whose
  // credential actually worked, because `agents[].channels` is undefined.
  const channels = agents.data?.channels ?? [];
  const allAgents = agents.data?.all_agents ?? [];

  // An archived agent still dials — 8loop does not block it — so its channels stay
  // selectable, but picking one is almost always a mistake and the list says so.
  const archivedAgentIds = useMemo(
    () => new Set(allAgents.filter((ag) => ag.archived).map((ag) => ag.id)),
    [allAgents],
  );
  // This channel's own agent first: the reader arrived from that agent's page, and its other
  // channels are the likeliest destination by a wide margin.
  const byAgent = useMemo(
    () => groupPickerByAgent(channels, archivedAgentIds, channel.agent_id),
    [channels, archivedAgentIds, channel.agent_id],
  );
  const blocked = channels.filter((c) => !c.dialable);
  const chosen = channels.find((c) => c.routing_identifier === form.dial_routing_identifier);
  const pointsAtItself =
    Boolean(chosen) && String(chosen!.id) === channel.channel_id;

  /** "Leave it dialling back on itself" — the default, and how an override is undone. */
  const keepingDefault = picked === SAME_CHANNEL;
  const removing = keepingDefault && Boolean(existing);

  /**
   * Three outcomes, one button.
   *
   * Choosing the default on a channel that has an override *removes* it — the same action the
   * "Back to itself" button on the row does, offered here because this is where somebody
   * changes their mind. Choosing it on a channel that has none is a no-op, and the button says
   * so instead of pretending to save something.
   */
  const submit = () => {
    if (removing) {
      del.mutate(channel.channel_id, {
        onSuccess: () => {
          toast.success(`Override removed — channel ${channel.channel_id} dials back on itself again.`);
          onClose();
        },
        onError: (e) => toast.error(errorMessage(e)),
      });
      return;
    }
    if (keepingDefault || !form.dial_routing_identifier) return;
    save.mutate(form, {
      onSuccess: () => {
        toast.success(`Callbacks from channel ${channel.channel_id} will dial out elsewhere.`);
        onClose();
      },
      onError: (e) => toast.error(errorMessage(e)),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            Send callbacks from {channel.channel_label ?? `channel ${channel.channel_id}`} elsewhere
          </DialogTitle>
          <DialogDescription>
            Only this one channel is affected. Everything else keeps dialling back on itself.
            {/*
              Say why this dialog is open when the answer is already known. The common
              case is arriving from the red alert above: the channel took the original
              call but 8loop refuses a dial on it, and repeating the reason here saves
              scrolling back to check which of the broken rows this was.
            */}
            {channel.dial.how === "unroutable" ? (
              <> Right now nothing can be dialled from it: {channel.dial.unroutable_reason}</>
            ) : channel.dial.dialable === false ? (
              <> Dialling back on this channel does not work: {channel.dial.blocked_reason}</>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {agents.isError && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Couldn't read this tenant's channels: {errorMessage(agents.error)}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="dial-channel" className="text-sm">
              Call back through
            </Label>
            <Select
              value={picked}
              onValueChange={(v) => {
                setPicked(v);
                if (v === SAME_CHANNEL) {
                  set("dial_routing_identifier", "");
                  set("dial_agent_id", null);
                  set("dial_agent_name", null);
                  set("dial_channel_label", null);
                  return;
                }
                const c = channels.find((x) => x.routing_identifier === v);
                set("dial_routing_identifier", v);
                set("dial_agent_id", c ? String(c.agent_id) : null);
                set("dial_agent_name", c?.agent_name ?? null);
                set("dial_channel_label", c?.display_name ?? null);
              }}
            >
              <SelectTrigger id="dial-channel" className="hover:bg-accent">
                <SelectValue
                  placeholder={agents.isPending ? "Loading channels…" : "Choose a channel"}
                />
              </SelectTrigger>
              <SelectContent>
                {/* The default, first and pickable. It is what happens with no override at
                    all, so it is not a blank state — it is a choice, and on a channel that
                    already has an override it is how you undo it. */}
                <SelectGroup>
                  <SelectLabel>The default</SelectLabel>
                  <SelectItem value={SAME_CHANNEL} className="cursor-pointer">
                    {channel.channel_label ?? `Channel ${channel.channel_id}`} — this same
                    channel, no override
                  </SelectItem>
                </SelectGroup>
                {byAgent.map(([agentName, list]) => (
                  <SelectGroup key={agentName}>
                    <SelectLabel>{agentName}</SelectLabel>
                    {list.map((c) => (
                      <SelectItem
                        key={c.id}
                        // Disabled items keep the channel findable and say why it cannot
                        // be used. `routing_identifier` can be null on a blocked
                        // channel, so the id backstops the value — Radix requires a
                        // non-empty one even for a disabled item.
                        value={c.routing_identifier ?? `blocked:${c.id}`}
                        disabled={!c.dialable || !c.routing_identifier}
                        className="cursor-pointer"
                      >
                        {channelName(c)}
                        {c.caller_id ? ` · ${c.caller_id}` : ""}
                        {c.dialable
                          ? archivedAgentIds.has(String(c.agent_id))
                            ? " — archived agent"
                            : ""
                          : " — unusable"}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>

            {chosen && (
              <div className="space-y-1 rounded-md border border-border bg-muted/40 px-3 py-2">
                <p className="text-xs">
                  Rings the customer from{" "}
                  <span className="font-mono">{chosen.caller_id ?? "an unknown number"}</span>
                  {chosen.provider_type && ` via ${chosen.provider_type}`}
                </p>
                <p className="font-mono text-[11px] text-muted-foreground">
                  {chosen.routing_identifier}
                </p>
                {chosen.warnings.map((w) => (
                  <p key={w} className="text-xs text-warning">
                    {w}
                  </p>
                ))}
              </div>
            )}

            {keepingDefault && (
              <p className="text-xs text-muted-foreground">
                {removing
                  ? "Saving removes the override that is on this channel, and callbacks from it go back to dialling out on itself."
                  : "This is what already happens, and it needs no override. Pick another channel above if this one cannot place the callback."}
              </p>
            )}

            {pointsAtItself && (
              <p className="text-xs text-warning">
                That is the same channel the callback came from, so it is the default with extra
                steps. Choose “this same channel, no override” at the top instead.
              </p>
            )}

            {blocked.length > 0 && (
              <details className="text-xs text-muted-foreground">
                {/* A pointer alone does not read as "this opens" on a line of grey 12px
                    text that looks like a caption. The hover colour and underline are
                    what make it look like the disclosure it is. */}
                <summary className="cursor-pointer hover:text-foreground hover:underline">
                  {blocked.length} channel{blocked.length === 1 ? " is" : "s are"} listed but not
                  selectable — why?
                </summary>
                <ul className="mt-1 space-y-1">
                  {blocked.map((c) => (
                    <li key={c.id}>
                      <span className="font-medium">{channelName(c)}</span>
                      {c.agent_name ? ` on ${c.agent_name}` : ""} — {c.blocked_reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              {/* `htmlFor`, so the sentence explaining the switch is also a hit target
                  for it. Without it the only target is a 36×20 rectangle at the other
                  end of the row. */}
              <Label htmlFor="override-enabled" className="text-sm">
                Enabled
              </Label>
              <p className="text-xs text-muted-foreground">
                Switched off, this channel goes back to dialling itself — the override is kept so
                you can switch it on again without rebuilding it.
              </p>
            </div>
            <Switch
              id="override-enabled"
              checked={form.enabled}
              onCheckedChange={(v) => set("enabled", v)}
            />
          </div>
        </div>

        <DialogFooter>
          {/*
            The button names what it will actually do, and is only dead in the one case
            where there is genuinely nothing to do — keeping a default that is already in
            force. It used to look ready with nothing chosen, and the only way to find out
            otherwise was to press it and read a toast.
          */}
          {keepingDefault && !removing && (
            <p className="mr-auto self-center text-[11px] text-muted-foreground">
              Nothing to save: this channel already dials back on itself.
            </p>
          )}
          <Button variant="ghost" onClick={onClose} disabled={save.isPending || del.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            variant={removing ? "outline" : "default"}
            disabled={
              save.isPending ||
              del.isPending ||
              (keepingDefault && !removing) ||
              (!keepingDefault && !form.dial_routing_identifier)
            }
            className="min-w-[120px]"
          >
            {save.isPending || del.isPending
              ? "Saving…"
              : removing
                ? "Remove the override"
                : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 8loop's own name for a channel. There is no `label` field — that was invented. */
function channelName(c: EightloopChannel): string {
  return c.display_name ?? `Channel ${c.id}`;
}

/**
 * Agents in a stable order, usable channels first inside each.
 *
 * `sourceAgentId` — the agent whose page the reader came from — is pinned to the top and
 * labelled as theirs. Its other channels are by far the likeliest destination: the case this
 * dialog exists for is a campaign-dialler channel that cannot place a call, and the fix is
 * usually the ordinary outbound channel sitting next to it on the same agent.
 *
 * Groups whose agent is archived, and groups with nothing selectable, sink to the bottom —
 * still present, because "why can't I pick X?" needs X to be findable.
 */
function groupPickerByAgent(
  channels: EightloopChannel[],
  archivedAgentIds: Set<string>,
  sourceAgentId?: string | null,
): [string, EightloopChannel[]][] {
  const groups = new Map<string, EightloopChannel[]>();
  const mine = new Set<string>();
  for (const c of channels) {
    const isSource = sourceAgentId != null && String(c.agent_id) === String(sourceAgentId);
    const name = isSource
      ? `This agent — ${c.agent_name ?? `Agent ${c.agent_id}`}`
      : (c.agent_name ?? `Agent ${c.agent_id}`);
    if (isSource) mine.add(name);
    const list = groups.get(name);
    if (list) list.push(c);
    else groups.set(name, [c]);
  }
  const rank = (name: string, list: EightloopChannel[]) =>
    (mine.has(name) ? -10 : 0) +
    (list.some((c) => c.dialable) ? 0 : 2) +
    (list.every((c) => archivedAgentIds.has(String(c.agent_id))) ? 1 : 0);
  return [...groups.entries()]
    .map(([name, list]) => {
      const sorted = [...list].sort((a, b) => Number(b.dialable) - Number(a.dialable));
      return [name, sorted] as [string, EightloopChannel[]];
    })
    .sort((a, b) => rank(a[0], a[1]) - rank(b[0], b[1]) || a[0].localeCompare(b[0]));
}
